import type { Preset, RequiredLighting } from "@/features/presets/params"
import type { TutorialKind } from "@/features/tutorial/Tutorial.svelte"

export const SOURCE = {
  FRONT_CAMERA: "front_camera",
  BACK_CAMERA: "back_camera",
  UPLOAD: "upload",
  COMPANION: "companion",
} as const
export type Source = (typeof SOURCE)[keyof typeof SOURCE]

export const QUALITY_CHECK = {
  OFF: "off",
  NORMAL: "normal",
  STRICT: "strict",
} as const

export type QualityCheck = (typeof QUALITY_CHECK)[keyof typeof QUALITY_CHECK]

export type Messages = {
  [key: string]: string | Messages
}

export type AppHost = Element | ShadowRoot

export const ENTRY_POINT = {
  TUTORIAL: "tutorial",
  CAPTURE: "capture",
} as const

export type EntryPoint = (typeof ENTRY_POINT)[keyof typeof ENTRY_POINT]

/** Options to configure LIQA‘s look and behavior. */
export type AppConfig = {
  /** The license key obtained from {@link https://haut.ai/#contacts Haut.ai }.  */
  license: string
  /** A string containing valid CSS rules. */
  styles?: string
  /** An object whose keys are translation keys and values are translation texts. */
  messages?: Messages
  /**
   * A preset used to customize AI quality criteria and user flow.
   * @default "face"
   */
  preset?: Preset
  /**
   * Overrides for the default preset‘s quality settings.
   * @hidden
   * @deprecated Use `requiredLighting`.
   */
  settings?: Partial<{ lighting: number }>
  /**
   * A list of sources to capture the image from.
   *
   * If "front_camera" is specified, the front camera will be used to capture the image.
   * If the device has no front camera but has a back camera - the back camera will be used as a fallback.
   *
   * If "back_camera" is specified, the back camera will be used to capture the image.
   * If the device has no back camera but has a front camera - the front camera will be used as a fallback.
   *
   * If more than one camera is specified as a source, the first one will be used as the initial one.
   *
   * If only one "upload" source is specified, the file upload will be used to capture the image.
   * @default ["front_camera"]
   */
  sources?: Source[]
  /**
   * Image capture mode for `"camera"` source.
   * `"auto"` means LIQA will automatically take a photo when all AI quality criteria met.
   * `"manual"` means LIQA will allow the user to take a photo on their own via a button.
   * The option is only applicable to "hair" preset. For other presets it is always "auto".
   */
  capture?: "auto" | "manual"
  /**
   * Lighting requirements for the image capture.
   * - `"none"`: no requirements are set and lighting will not be validated. (**WARNING**: the quality of the processing results may be affected)
   * - `"adaptive"`: the requirements will adapt to the lighting conditions.
   * - `"default"`: the minimum requirements will be set to optimal values which will provide more details on the photo.
   *
   * Default value: `"default"`
   */
  requiredLighting?: RequiredLighting
  /**
   * Image post-processing settings.
   * - `"original"`: the image from `"capture"` will not be processed.
   * - `"anonymized"`: the image from `"capture"` will be anonymized. (only skin mask will be returned)
   *
   * @default "original"`
   */
  postprocessing?: "original" | "anonymized"
  /**
   * A flag to enable or disable the effects during the image capture process.
   *
   * @default false
   */
  effects?: boolean
  /**
   * A flag to enable or disable tutorial option on the source selection screen.
   * Enabled only when sources option contain "front_camera" or "back_camera" and "upload".
   *
   * - `"off"`: no tutorial will be shown.
   * - `"video"`: a video tutorial will be shown.
   * - `"image"`: an image tutorial will be shown. Can be used as a fallback for reducing the loading time.
   *
   * @default "off"
   */
  tutorial?: TutorialKind
  /**
   * A flag to set the entry point of the LIQA.
   *
   * - `"capture"`: the entry point will be set to the capture screen.
   * - `"tutorial"`: the entry point will be set to the tutorial screen.
   *
   * @default "capture"
   */
  entryPoint?: EntryPoint
  /**
   * A flag to set the quality check behavior.
   *
   * - `off`: the quality check will be disabled.
   * - `strict`: the quality check will be strict and the user will not be able to continue if the quality criteria are not met.
   * - `normal`: the quality check will be normal and the user will be able to continue even if the quality criteria are not met.
   *
   * @default "off"
   */
  qualityCheck?: QualityCheck
  /**
   * An array specifying which capture sources should enforce quality checks.
   *
   * The quality check behavior (set via the `qualityCheck` property) will only be applied for the sources included in this array.
   * Available values are:
   * - `front_camera`: Enforces quality check on images captured from the front camera.
   * - `back_camera`: Enforces quality check on images captured from the back camera.
   * - `upload`: Enforces quality check on images provided via file upload.
   * - `companion`: Enforces quality check on images captured via a companion source.
   *
   * If not provided, quality checks will be applied for all sources.
   *
   * @default ["front_camera","back_camera","upload","companion"]
   */
  qualityCheckSources?: Source[]
  /**
   * A flag to enable or disable best frame selection during image capture.
   *
   * When enabled, LIQA will track multiple frames during the capture process
   * and automatically select the frame with the highest quality metrics.
   *
   * @default true
   */
  bestFrameSelection?: boolean
}
